import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, Pencil, Check, X, RefreshCw, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-lg border border-xmove-ink/15 bg-xmove-cream px-3 py-2 text-sm text-xmove-ink outline-none transition-colors focus:border-xmove-orange disabled:border-transparent disabled:bg-transparent disabled:px-0 disabled:text-xmove-ink";

const Campo = ({ label, value, onChange, edit, max, rows, testid }) => (
  <label className="block">
    <span className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-xmove-muted">
      {label}
      {max && <span className={value.length > max ? "text-red-500" : ""}>{value.length}/{max}</span>}
    </span>
    {rows ? (
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} disabled={!edit} className={`${inputCls} resize-y leading-relaxed`} data-testid={testid} />
    ) : (
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} disabled={!edit} className={`${inputCls} font-semibold`} data-testid={testid} />
    )}
  </label>
);

export const TestiSeoModal = ({ veicolo, onClose, onPublished }) => {
  const [testi, setTesti] = useState(null);
  const [generating, setGenerating] = useState(true);
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const genera = async () => {
    setGenerating(true);
    setEdit(false);
    try {
      const r = await axios.post(`${API}/admin/veicoli/${veicolo.id_motorizzazione}/rigenera-testi`);
      setTesti(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generazione non riuscita, riprova.");
      onClose();
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => { genera(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setTesti({ ...testi, [k]: v });
  const setFaq = (i, k, v) => setTesti({ ...testi, faqs: testi.faqs.map((f, j) => (j === i ? { ...f, [k]: v } : f)) });

  const pubblica = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/veicoli/${veicolo.id_motorizzazione}/testi`, testi);
      toast.success(`Testi pubblicati su ${veicolo.marca} ${veicolo.modello}.`);
      onPublished();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pubblicazione non riuscita, riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-white" data-testid="testi-seo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl text-xmove-ink">
            <Sparkles size={18} className="text-xmove-orange" /> Testo SEO — {veicolo.marca} {veicolo.modello} {veicolo.allestimento}
          </DialogTitle>
          <DialogDescription className="text-xs text-xmove-muted">
            {generating
              ? "L'AI sta scrivendo titolo, meta, descrizione e FAQ con i dati attuali del veicolo…"
              : edit
                ? "Correggi liberamente i testi, poi premi OK per pubblicarli sulla pagina."
                : "Anteprima: controlla il testo. Premi Modifica per correggerlo oppure OK per pubblicarlo."}
          </DialogDescription>
        </DialogHeader>

        {generating || !testi ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-xmove-muted" data-testid="testi-seo-loading">
            <Loader2 size={26} className="animate-spin text-xmove-orange" /> Generazione in corso (circa 20 secondi)…
          </div>
        ) : (
          <div className="space-y-4" data-testid="testi-seo-preview">
            <Campo label="Titolo pagina (H1)" value={testi.h1} onChange={set("h1")} edit={edit} testid="testi-h1" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo label="Meta title" value={testi.meta_title} onChange={set("meta_title")} edit={edit} max={70} testid="testi-meta-title" />
              <Campo label="Meta description" value={testi.meta_description} onChange={set("meta_description")} edit={edit} max={165} rows={3} testid="testi-meta-description" />
            </div>
            <Campo label="Descrizione" value={testi.descrizione} onChange={set("descrizione")} edit={edit} rows={8} testid="testi-descrizione" />

            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-xmove-muted">FAQ ({testi.faqs.length})</p>
              <ol className="space-y-3">
                {testi.faqs.map((f, i) => (
                  <li key={i} className="rounded-xl border border-xmove-ink/8 bg-xmove-cream/50 p-3" data-testid={`testi-faq-${i}`}>
                    <div className="flex items-start gap-2">
                      <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-xmove-orange text-[10px] font-bold text-white">{i + 1}</span>
                      <div className="flex-1 space-y-1.5">
                        <input type="text" value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} disabled={!edit} className={`${inputCls} font-bold`} data-testid={`testi-faq-q-${i}`} />
                        <textarea rows={2} value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} disabled={!edit} className={`${inputCls} resize-y leading-relaxed`} data-testid={`testi-faq-a-${i}`} />
                      </div>
                      {edit && (
                        <button type="button" onClick={() => setTesti({ ...testi, faqs: testi.faqs.filter((_, j) => j !== i) })} title="Rimuovi FAQ" data-testid={`testi-faq-remove-${i}`} className="rounded-md border border-red-200 p-1.5 text-red-400 transition-colors hover:bg-red-500 hover:text-white">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
              {edit && testi.faqs.length < 5 && (
                <button type="button" onClick={() => setTesti({ ...testi, faqs: [...testi.faqs, { q: "", a: "" }] })} data-testid="testi-faq-add" className="mt-2 flex items-center gap-1.5 rounded-lg border border-dashed border-xmove-ink/25 px-3 py-1.5 text-xs font-bold text-xmove-ink transition-colors hover:border-xmove-orange hover:text-xmove-orange">
                  <Plus size={13} /> Aggiungi FAQ
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-xmove-ink/8 pt-4">
              <button type="button" onClick={genera} data-testid="testi-rigenera" className="flex items-center gap-1.5 rounded-lg border border-xmove-ink/15 px-3 py-2 text-xs font-bold text-xmove-muted transition-colors hover:border-xmove-night hover:text-xmove-night">
                <RefreshCw size={13} /> Rigenera
              </button>
              <span className="flex-1" />
              <button type="button" onClick={onClose} data-testid="testi-annulla" className="flex items-center gap-1.5 rounded-lg border border-xmove-ink/15 px-4 py-2 text-xs font-bold text-xmove-muted transition-colors hover:text-xmove-ink">
                <X size={13} /> Annulla
              </button>
              <button type="button" onClick={() => setEdit(!edit)} data-testid="testi-modifica" className={`flex items-center gap-1.5 rounded-lg border px-4 py-2 text-xs font-bold transition-colors ${edit ? "border-xmove-night bg-xmove-night text-white" : "border-xmove-ink/15 text-xmove-ink hover:border-xmove-night"}`}>
                <Pencil size={13} /> {edit ? "Fine modifica" : "Modifica"}
              </button>
              <button type="button" onClick={pubblica} disabled={saving} data-testid="testi-ok" className="flex items-center gap-1.5 rounded-lg bg-xmove-orange px-5 py-2 text-xs font-bold text-white transition-colors hover:bg-xmove-orangeDark disabled:opacity-60">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} OK
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
